import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  Paper,
  Group,
  Button,
  Badge,
  Grid,
  Card,
  ThemeIcon,
  Table,
  Pagination,
  Stack,
  Skeleton,
  Avatar,
  Box,
  RingProgress,
  Center,
  Divider,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconClock,
  IconChecklist,
  IconUser,
  IconCalendar,
  IconEdit,
  IconEye,
  IconActivity,
  IconSpeakerphone,
} from "@tabler/icons-react";
import { Endpoints } from "../../../../lib/endpoints";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../../hooks/usePerformRequest";
import { PaginatedResponse } from "../../../../lib/types";
import { Campaign } from "../index"; // Import Campaign type from list page or define here if not exported

// Define Mission type locally if not available
interface Mission {
  id: string; // API returns 'id'
  _id?: string; // Sometimes Mongoose returns _id
  title: string;
  type: string;
  status: "active" | "completed" | "draft";
  points: number;
  completions: number;
  targetCompletions: number;
  requiredListens?: number;
}

const CampaignDetails = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();

    // Campaign Data State
  const campaignEndpoint = useMemo(
    () =>
      campaignId
        ? Endpoints.GetCampaign(campaignId)
        : { route: "", method: "GET" as const },
    [campaignId],
  );

  const { data: campaignResponse, isLoading: campaignLoading } =
    usePerformRequest<{
      data: Campaign;
    }>({
      route: campaignEndpoint.route,
      method: "GET",
    });

  const campaign = campaignResponse?.data;

  // Set document title
  useEffect(() => {
    if (campaign?.title) {
      document.title = `${campaign.title} | Campaign Details | Virel Admin`;
    } else {
      document.title = "Campaign Details | Virel Admin";
    }
  }, [campaign]);

  // Activities (Missions) State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const missionsEndpoint = useMemo(
    () =>
      campaignId
        ? Endpoints.GetMissions({
            campaign: campaignId,
            page: currentPage,
            limit: pageSize,
          })
        : { route: "", method: "GET" as const },
    [campaignId, currentPage, pageSize],
  );

  const { data: missionsResponse, isLoading: missionsLoading } =
    usePerformRequest<PaginatedResponse<Mission[]>>({
      route: missionsEndpoint.route,
      method: "GET",
    });

  const missionsData = missionsResponse?.data;
  const missions = Array.isArray(missionsData) ? missionsData : [];
  const totalMissions =
    (missionsResponse as any)?.totalRecords ??
    (missionsResponse?.data as any)?.totalRecords ??
    missions.length;
  const totalPages = Math.max(1, Math.ceil(totalMissions / pageSize));

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge color="green">Active</Badge>;
      case "completed":
        return <Badge color="green">Completed</Badge>;
      default:
        return <Badge color="gray">Draft</Badge>;
    }
  };

  const getMissionStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return (
          <Badge color="green" variant="light">
            Active
          </Badge>
        );
      case "completed":
        return (
          <Badge color="green" variant="light">
            Completed
          </Badge>
        );
      default:
        return (
          <Badge color="gray" variant="light">
            Draft
          </Badge>
        );
    }
  };

  if (campaignLoading) {
    return (
      <Container size="xl" py="xl">
        <Skeleton height={50} width={200} mb="xl" />
        <Skeleton height={200} mb="md" />
        <Skeleton height={400} />
      </Container>
    );
  }

  if (!campaign) {
    return (
      <Container size="xl" py="xl">
        <Title>Campaign Not Found</Title>
        <Button mt="md" onClick={() => navigate("/d/campaigns")}>
          Back to Campaigns
        </Button>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      {/* Header */}
      <Group justify="space-between" align="flex-start" mb="xl">
        <div>
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/d/campaigns")}
            mb="xs"
            pl={0}
          >
            Back to Campaigns
          </Button>
          <Group align="center" gap="md">
            <Title order={1}>{campaign.title}</Title>
            {getStatusBadge(campaign.status)}
          </Group>
          <Text c="dimmed" size="lg" mt={4}>
            {campaign.clientName}
          </Text>
        </div>
        <Group>
          <Button
            variant="light"
            leftSection={<IconEdit size={16} />}
            onClick={() => navigate(`/d/campaigns/update/${campaign._id}`)}
          >
            Edit Campaign
          </Button>
        </Group>
      </Group>

      <Grid gutter="xl">
        {/* Main Content */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          {/* Activities Table */}
          <Paper withBorder radius="md" p="md">
            <Group justify="space-between" mb="md">
              <Title order={3}>Activities</Title>
              <Text c="dimmed" size="sm">
                Activities associated with this campaign
              </Text>
              <Button
                variant="light"
                size="xs"
                onClick={() =>
                  navigate(`/d/missions/new?campaign=${campaign._id}`)
                } // Assuming we can prefill
              >
                Add Activity
              </Button>
            </Group>

            <Table verticalSpacing="sm" striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Progress</Table.Th>
                  <Table.Th>Earnings</Table.Th>
                  <Table.Th>Action</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {missionsLoading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <Table.Tr key={i}>
                      <Table.Td>
                        <Skeleton height={20} width={120} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={20} width={60} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={20} width={60} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={20} width={80} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={20} width={40} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={20} width={20} />
                      </Table.Td>
                    </Table.Tr>
                  ))
                ) : missions.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6} align="center">
                      <Text c="dimmed" py="xl">
                        No activities found for this campaign.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  missions.map((mission) => (
                    <Table.Tr
                      key={mission.id || mission._id}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(`/d/missions/${mission.id || mission._id}`)
                      }
                    >
                      <Table.Td>
                        <Text fw={500}>{mission.title}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          variant="outline"
                          color="gray"
                          size="sm"
                          tt="uppercase"
                        >
                          {mission.type}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {getMissionStatusBadge(mission.status)}
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <Text size="sm">
                            {mission.completions} / {mission.targetCompletions}
                          </Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <IconActivity size={14} color="orange" />
                          <Text size="sm">{mission.points}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <ThemeIcon variant="subtle" color="gray">
                          <IconEye size={16} />
                        </ThemeIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>

            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination
                  value={currentPage}
                  onChange={setCurrentPage}
                  total={totalPages}
                />
              </Group>
            )}
          </Paper>
        </Grid.Col>

        {/* Sidebar */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="md">
            <Card withBorder radius="md" p="md">
              <Stack align="center" gap="md">
                <div
                  style={{
                    width: 100,
                    height: 100,
                    borderRadius: 16,
                    backgroundColor: campaign.branding?.primaryColor || "#ccc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {campaign.branding?.logoUrl ? (
                    <img
                      src={campaign.branding.logoUrl}
                      alt=""
                      style={{ width: 60, height: 60, objectFit: "contain" }}
                    />
                  ) : (
                    <IconSpeakerphone size={48} color="white" />
                  )}
                </div>
                <Text fw={700} size="lg">
                  {campaign.clientName}
                </Text>
              </Stack>
            </Card>

            <Card withBorder radius="md" p="md">
              <Text fz="xs" tt="uppercase" fw={700} c="dimmed" mb="md">
                Campaign Details
              </Text>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Created
                  </Text>
                  <Text size="sm">
                    {new Date(campaign.createdAt).toLocaleDateString()}
                  </Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Eligibility
                  </Text>
                  <Text size="sm">
                    {campaign.eligibility?.type === "criteria"
                      ? "Restricted"
                      : "Open"}
                  </Text>
                </Group>
                {campaign.eligibility?.type === "criteria" && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Min Completions
                    </Text>
                    <Text size="sm">
                      {campaign.eligibility.minMissionCompletions}
                    </Text>
                  </Group>
                )}
                <Divider />
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Completion Bonus
                  </Text>
                  <Text size="sm">
                    {campaign.completionBonus
                      ? `${campaign.completionBonus.amount} ${
                          campaign.completionBonus.currency === "COIN" ||
                          campaign.completionBonus.currency === "NGN"
                            ? "Coins"
                            : campaign.completionBonus.currency
                        }`
                      : "-"}
                  </Text>
                </Group>
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>
    </Container>
  );
};

export default CampaignDetails;
